import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AppConfigModel } from '@libs/cms/models';
import { ConsentConfigModel, EnvironmentService } from '@libs/shared/common';
import { footerImprintNavItem, footerPrivacyNavItem } from '@libs/shared/models';
import { filter, firstValueFrom } from 'rxjs';
import { AppConfigService } from '../app-config';
import { TrackingConsentService } from './tracking-consent.service';

const CONSENT_SUPPRESSED_PATHS: readonly string[] = [footerImprintNavItem.url as string, footerPrivacyNavItem.url as string];

interface ConsentInitializeOptions {
  syncRouteSuppression?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ConsentManagerService {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly document: Document = inject(DOCUMENT);
  private readonly env: EnvironmentService = inject(EnvironmentService);
  private readonly appConfigService: AppConfigService = inject(AppConfigService);
  private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly router: Router = inject(Router);
  private readonly trackingConsent: TrackingConsentService = inject(TrackingConsentService);
  private consentInitialized: boolean = false;

  private get browserWindow(): Window | null {
    return this.isBrowser ? this.document.defaultView : null;
  }

  public showSettings(): void {
    this.browserWindow?.UC_UI?.showSecondLayer();
  }

  public initializeForPage(hidden?: boolean): void {
    const view: Window | null = this.browserWindow;

    if (view) {
      view.UC_UI_SUPPRESS_CMP_DISPLAY = hidden === true;
      this.showConsentIfRequired(view);
    }

    void firstValueFrom(this.appConfigService.getConfig()).then((config: AppConfigModel) => {
      const usercentricsId: string | undefined = config.settings?.usercentricsId;

      if (!usercentricsId) {
        return;
      }

      this.initialize({
        settingId: usercentricsId,
        version: undefined,
      }, {
        syncRouteSuppression: false,
      });
    });
  }

  public initialize(config?: ConsentConfigModel, options: ConsentInitializeOptions = {}): void {
    const consentConfig: ConsentConfigModel | undefined = this.getConsentConfig(config);
    const view: Window | null = this.browserWindow;

    if (!view || this.consentInitialized || !consentConfig) {
      return;
    }

    this.consentInitialized = true;

    this.trackingConsent.observeUsercentrics();

    if (options.syncRouteSuppression !== false) {
      this.syncRouteConsentBannerSuppression(view);
    }

    this.createPreconnectScriptTags();

    const scriptTag: HTMLScriptElement = this.document.createElement('script');

    scriptTag.id = 'usercentrics-cmp';
    scriptTag.src = 'https://app.usercentrics.eu/browser-ui/latest/loader.js';
    scriptTag.setAttribute('data-settings-id', consentConfig.settingId);
    scriptTag.setAttribute('async', '');

    if (consentConfig.version) {
      scriptTag.setAttribute('data-version', consentConfig.version);
    }

    this.document.head.appendChild(scriptTag);
  }

  private getConsentConfig(config?: ConsentConfigModel): ConsentConfigModel | undefined {
    const envConsentConfig: ConsentConfigModel | undefined = this.env.consentConfig();

    if (!envConsentConfig || !config) {
      return envConsentConfig;
    }

    return {
      settingId: config.settingId,
      version: config.version ?? envConsentConfig?.version,
    };
  }

  private createPreconnectScriptTags(): void {
    const preconnectUrls: string[] = ['//app.usercentrics.eu', '//api.usercentrics.eu'];

    for (const preconnectUrl of preconnectUrls) {
      const linkElement: HTMLLinkElement = this.document.createElement('link');

      linkElement.rel = 'preconnect';
      linkElement.href = preconnectUrl;
      this.document.head.appendChild(linkElement);
    }

    const preloadLink: HTMLLinkElement = this.document.createElement('link');

    preloadLink.rel = 'preload';
    preloadLink.href = '//app.usercentrics.eu/browser-ui/latest/loader.js';
    preloadLink.setAttribute('as', 'script');

    this.document.head.appendChild(preloadLink);
  }

  private syncRouteConsentBannerSuppression(view: Window): void {
    this.updateConsentBannerSuppression(view, this.router.url);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        this.updateConsentBannerSuppression(view, event.urlAfterRedirects);
        this.showConsentIfRequired(view);
      });
  }

  private showConsentIfRequired(view: Window): void {
    if (!view.UC_UI_SUPPRESS_CMP_DISPLAY && view.UC_UI?.isConsentRequired()) {
      view.UC_UI.showFirstLayer();
    }
  }

  private updateConsentBannerSuppression(view: Window, url: string): void {
    const urlPath: string = url.split('?')[0];

    view.UC_UI_SUPPRESS_CMP_DISPLAY = CONSENT_SUPPRESSED_PATHS.some(path => urlPath === path);
  }
}
